import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "../../../components/forms";
import * as Yup from "yup";
import OTPField from "./OTPField";
import { useLazySubmitOtpCodeQuery } from "../../../services/callbackService";
import { toast } from "react-toastify";
import parseError from "../../../utils/errorUtils";

const OTPVerificationPopup = ({ show, onClose, reSendOtp, count, setCount }) => {
  const navigate = useNavigate();
  const [resendDisabled, setResendDisabled] = useState(true);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [validationError, setValidationError] = useState(false);
  const [submitOtpCode] = useLazySubmitOtpCodeQuery();

  const savedValues = JSON.parse(
    localStorage.getItem("EligibilityForm") || "null"
  );

  const otpValidationSchema = Yup.object().shape({
    otp: Yup.array()
      .of(
        Yup.string()
          .matches(/^\d$/, "Each field must be a single digit")
          .required("All OTP fields are required")
      )
      .test("complete", "All OTP fields are required", (value) =>
        value.every((field) => field !== "")
      ),
  });

  useEffect(() => {
    if (show) {
      const savedTimestamp = localStorage.getItem("otpTimestamp");
      const currentTime = Date.now();

      if (savedTimestamp) {
        const elapsedSeconds = Math.floor((currentTime - Number(savedTimestamp)) / 1000);
        const remainingTime = Math.max(900 - elapsedSeconds, 0);
        setCount(remainingTime);
  
        setResendDisabled(elapsedSeconds < 60);
        if (elapsedSeconds < 60) {
          setTimeout(() => setResendDisabled(false), (60 - elapsedSeconds) * 1000);
        }
      } else {
        setCount(900);
        localStorage.setItem("otpTimestamp", currentTime);
  
        setResendDisabled(true);
        setTimeout(() => setResendDisabled(false), 60000);
      }
    }
  }, [show]);

  useEffect(() => {
    if (count > 0) {
      const timer = setInterval(() => {
        setCount((prev) => {
          if (prev === 1) {
            clearInterval(timer);
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [count]);

  const handleResendOtp = async () => {
    if (!resendDisabled) {
      await reSendOtp();
      setCount(900);
      localStorage.setItem("otpTimestamp", Date.now());
  
      setResendDisabled(true);
      setTimeout(() => {
        setResendDisabled(false);
      }, 60000);
    }
  };

  if (!show) return null;

  const handleNavigate = () => navigate("/onboarding/eligibility-flow/age", { replace: true });

  const handleSubmit = async () => {
    if (otpValues.every((value) => value !== "")) {
      try {
        const res = await submitOtpCode({
          phoneNumber: savedValues?.["Phone number"],
          otp: otpValues.join(""),
        });
        if (!res.data.data) {
          toast.error(parseError(res.error, "Invalid verification code"));
        } else {
          localStorage.setItem("isOtpVerified", true);
          handleNavigate();
        }
      } catch (error) { }
    } else {
      setValidationError(true);
    }
  };

  const minutes = Math.floor(count / 60);
  const seconds = count % 60;

  return (
    <Form
      initialValues={{ otp: ["", "", "", "", "", ""] }}
      validationSchema={otpValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      className="d-flex"
    >
      <div className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-50 popup">
        <div className="bg-white rounded p-4 pb-5 shadow-lg inner-box">
          <div className="d-flex flex-row align-items-center justify-content-between mx-1 mx-md-4">
            <h5 className="text-primary-600 mb-0 fw-bold">
              Verify your phone number{" "}
            </h5>
            <button
              onClick={onClose}
              className="d-flex bg-transparent border-0 align-items-center"
            >
              <img
                src="/images/vectors/close-circle.svg"
                alt="close"
                className="google-icon"
              />
            </button>
          </div>
          <hr className="hr-popup mt-4 mb-5" />
          <div className="modal-body mx-1 mx-md-4">
            <div className="container px-0 d-flex flex-column justify-content-between">
              <p className="mb-1">
                We just sent you a message with a verification code. Please
                enter it below.{" "}
              </p>
              <p className="d-flex flex-row gap-1 text-gray-1 mb-2 mt-5">
                Code expires in{" "}
                <span className="text-primary-600 fw-bold">
                  {String(minutes).padStart(2, "0")}:
                  {String(seconds).padStart(2, "0")}
                </span>
              </p>
              <OTPField length={6} values={otpValues} onChange={setOtpValues} />
              {validationError && (
                <label className="text-danger mt-3">
                  Please fill in all 6 digits of the verification code.
                </label>
              )}
              <p className="text-gray-1 mb-5 mt-4">
                Didn’t receive the code? You can resend it after 1 minute.{" "}
                <span className={`fw-bold text-decoration-underline ms-0 ms-md-1 ${resendDisabled ? "resend-disabled-text" : "text-primary-600 cursor-pointer"}`}
                  onClick={!resendDisabled ? handleResendOtp : null}
                >
                  Re-send
                </span>
              </p>
            </div>
            <div className="d-flex flex-column-reverse flex-md-row justify-content-end align-items-center px-5 px-md-0 py-4 py-md-0">
              <button
                className="bg-primary-600 text-white py-2 px-5 w-fit h-fit fw-bold fs-6 border-0 rounded text-nowrap"
                onClick={handleSubmit}
              >
                Verify phone number
              </button>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default OTPVerificationPopup;
